import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { initGA, trackPageView } from '../utils/analytics';

const SEO = ({
  title = "Estação Marina - Centro Comercial",
  description = "Centro comercial moderno com lojas, restaurantes e serviços no Altiplano Leste - Paranoá. Descubra as melhores ofertas, eventos e experiências na Estação Marina da Rocha.",
  keywords = "centro comercial, lojas, restaurantes, Altiplano Leste, Paranoá, Brasília, shopping, eventos, promoções",
  canonical = "",
  ogImage = "/og-image.jpg",
  ogType = "website",
  twitterCard = "summary_large_image",
  structuredData = null,
  noindex = false,
  nofollow = false
}) => {
  const siteUrl = "https://estacaomarina.com.br";
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  
  const metaRobots = [];
  if (noindex) metaRobots.push('noindex');
  if (nofollow) metaRobots.push('nofollow');
  if (metaRobots.length === 0) metaRobots.push('index', 'follow');

  // Inicializa Google Analytics
  useEffect(() => {
    initGA();
    trackPageView(fullCanonical);
  }, [fullCanonical]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={metaRobots.join(', ')} />
      <link rel="canonical" href={fullCanonical} />
      
      {/* Language and Locale */}
      <html lang="pt-BR" />
      <meta name="language" content="pt-BR" />
      <meta name="geo.region" content="BR-DF" />
      <meta name="geo.placename" content="Brasília" />
      <meta name="geo.position" content="-15.7801234;-47.8901234" />
      <meta name="ICBM" content="-15.7801234, -47.8901234" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Estação Marina" />
      <meta property="og:locale" content="pt_BR" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@estacaomarina" />
      <meta name="twitter:creator" content="@estacaomarina" />
      
      {/* Mobile and App */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="theme-color" content="#4c3932" />
      <meta name="msapplication-TileColor" content="#4c3932" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Estação Marina" />
      
      {/* Business Information */}
      <meta name="author" content="Estação Marina da Rocha" />
      <meta name="publisher" content="Estação Marina da Rocha" />
      <meta name="copyright" content="Estação Marina da Rocha" />
      <meta name="reply-to" content="estacaocrdf@gmail.com" />
      
      {/* Additional SEO */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="7 days" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
